import { useEffect } from 'react';

import { refresh } from '../api/auth';
import { useAuthStore } from '../stores/authStore';

// Refresh timer (PRD §9.4 + docs/frontend_architecture.md §6).
//
// Schedules a single setTimeout to fire 30s before `expiresAt`. When
// the access token rotates (login, manual refresh, or an earlier
// scheduled refresh) the store's `expiresAt` changes and the effect
// re-runs, replacing the timer.
//
// 30s of head-room covers clock skew between client and server plus the
// round-trip itself. If the page is backgrounded long enough for the
// timer to miss, the 401 interceptor (B1e PR3) catches up on the next
// authenticated request.
//
// On refresh failure we swallow the error here — the user is mid-page
// and we don't want an unhandled rejection surfacing in the console.
// The next 401 will trigger the redirect-to-login flow when PR3 lands;
// until then the request that needs auth simply 401s and the call site
// surfaces it.

const REFRESH_LEAD_MS = 30_000;

export function useAuthRefreshTimer(): void {
  const expiresAt = useAuthStore((state) => state.expiresAt);

  useEffect(() => {
    if (expiresAt === null) return;
    const delay = Math.max(0, expiresAt - REFRESH_LEAD_MS - Date.now());
    const id = setTimeout(() => {
      void refresh().catch(() => {
        // Intentionally swallowed — see file header.
      });
    }, delay);
    return () => {
      clearTimeout(id);
    };
  }, [expiresAt]);
}
